// ═══════════════════════════════════════════════════════════════════════════
// ORE LAVORATE SU UNA COMMESSA — dalla stessa fonte del Controllo Economico (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Ogni azienda sceglie in Impostazioni Aziendali da dove arrivano le ore che diventano costo
// di manodopera (fontOreEconomico): Programmazione settimanale, Rapportini o Presenze
// approvate. Le ore mostrate fuori dal Controllo Economico (Cabina di Regia) devono venire
// dalla STESSA fonte, altrimenti la stessa giornata vale 2 ore nei costi e 8 ore altrove.
// Le altre registrazioni (rapportini, Giornale di cantiere) servono come riscontro: se un
// rapportino indica ore diverse da quelle contabilizzate per la stessa persona e lo stesso
// giorno, lo si segnala.
// Ogni voce: { chiave, nome, data: "aaaa-mm-gg", ore, rif }.
// ═══════════════════════════════════════════════════════════════════════════
import { numIt, dataMovimento } from "./economiaCommessa";
import { orePerOperatoreRapportino } from "./oreRapportini";
import { uidConDiario, oreDaDiario, senzaPersoneDiario } from "./diarioOre";

export const FONTI_ORE = {
  programmazione: "Programmazione settimanale",
  rapportini: "Rapportini di intervento",
  presenze: "Presenze (timbratura)",
};

/** Fonte delle ore impostata dall'azienda (predefinita: Programmazione, come il Controllo Economico). */
export const fonteOreAzienda = (org) => (FONTI_ORE[org?.fontOreEconomico] ? org.fontOreEconomico : "programmazione");

const norm = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");
const isoData = (s) => (/^\d{4}-\d{2}-\d{2}$/.test(String(s || "")) ? s : "");

/**
 * Programmazione settimanale: celle "uid_riga_aaaa-mm-gg" (o "uid_aaaa-mm-gg") con una o più
 * voci { commessaId, ore, dipNome }. Solo le voci di questa commessa con ore > 0.
 * @param nomi - mappa uid → nome (dagli utenti), per le voci senza dipNome
 */
export function oreDaProgrammazione(programmazioni = [], projectId, nomi = {}) {
  const voci = [];
  programmazioni.forEach(p => {
    Object.entries(p.celle || {}).forEach(([chiaveCella, voce]) => {
      const data = (chiaveCella.match(/(\d{4}-\d{2}-\d{2})$/) || [])[1] || "";
      const uid = chiaveCella.split("_")[0];
      (Array.isArray(voce) ? voce : voce && typeof voce === "object" ? [voce] : []).forEach(v => {
        if (v.commessaId !== projectId) return;
        const ore = numIt(v.ore);
        if (!(ore > 0)) return;
        const u = v.nexoraUid || uid;
        voci.push({ chiave: u || norm(v.dipNome), uidCella: uid, nome: (v.dipNome || "").trim() || nomi[u] || "Collaboratore", data, ore, rif: "Programmazione" });
      });
    });
  });
  return voci;
}

/** Rapportini: ore di ciascun operatore (utils/oreRapportini.js). */
export function oreDaRapportini(rapportini = [], modoPrecedenti = "durata") {
  const voci = [];
  rapportini.forEach(r => {
    const data = isoData(r.data);
    orePerOperatoreRapportino(r, modoPrecedenti).forEach(p => {
      if (!(p.ore > 0)) return;
      voci.push({ chiave: p.uid || norm(p.nome), nome: p.nome || "Collaboratore", data, ore: p.ore, rif: r.numero || "Rapportino" });
    });
  });
  return voci;
}

/**
 * Presenze: movimenti di manodopera scritti dalle timbrature (in tempo reale) e confermati
 * all'approvazione del cartellino. La persona si riconosce dall'account (presenzeRef =
 * orgId_uid_anno_mese), così coincide con l'operatore del rapportino anche se il nome è
 * scritto in modo diverso; la data viene da utils/economiaCommessa.js (dataMovimento).
 */
export function oreDaPresenze(movimenti = []) {
  return movimenti.filter(m => m.categoriaId === "manodopera" && m.presenzeRef && !m.eliminato).map(m => {
    const parti = String(m.presenzeRef).split("_");
    const uid = parti.length >= 4 ? parti[parti.length - 3] : "";
    const nome = m.dati?.dipendente || "Collaboratore";
    return { chiave: uid || norm(nome), nome, data: dataMovimento(m), ore: numIt(m.ore), rif: "Presenze" };
  }).filter(v => v.ore > 0);
}

/**
 * Riepilogo per la commessa dalla fonte ufficiale: giornate-uomo (persona × giorno), ore,
 * persone; più le differenze con i rapportini quando la fonte non sono i rapportini.
 */
export function riepilogoOre(vociFonte = [], { rapportini = [], fonte = "programmazione" } = {}) {
  const giorni = new Map(); // chiave|data → { nome, data, ore }
  vociFonte.forEach(v => {
    const k = `${v.chiave}|${v.data}`;
    const g = giorni.get(k) || { chiave: v.chiave, nome: v.nome, data: v.data, ore: 0 };
    g.ore += v.ore; giorni.set(k, g);
  });
  const elenco = [...giorni.values()];
  const persone = new Map();
  elenco.forEach(g => {
    const p = persone.get(g.chiave) || { nome: g.nome, giornate: 0, ore: 0, ultima: "" };
    p.giornate++; p.ore += g.ore; if (g.data > p.ultima) p.ultima = g.data; persone.set(g.chiave, p);
  });
  // Riscontro: ore dei rapportini diverse da quelle contabilizzate (stessa persona, stesso giorno)
  const differenze = [];
  if (fonte !== "rapportini") {
    const perGiorno = new Map();
    rapportini.forEach(v => { const k = `${v.chiave}|${v.data}`; const x = perGiorno.get(k) || { ...v, ore: 0, rif: [] }; x.ore += v.ore; x.rif.push(v.rif); perGiorno.set(k, x); });
    perGiorno.forEach((r, k) => {
      if (!r.data) return;
      const contab = giorni.get(k)?.ore || 0;
      if (Math.abs(contab - r.ore) >= 0.25) differenze.push({ nome: r.nome, data: r.data, oreRapportino: r.ore, oreContabilizzate: contab, rif: r.rif.join(", ") });
    });
    differenze.sort((a, b) => b.data.localeCompare(a.data));
  }
  return {
    giornate: elenco.length,
    ore: Math.round(elenco.reduce((s, g) => s + g.ore, 0) * 100) / 100,
    elenco,
    persone: [...persone.values()].sort((a, b) => b.ultima.localeCompare(a.ultima) || b.giornate - a.giornate),
    differenze,
  };
}

/**
 * Diario «Le mie ore» (W403): per le persone «dal diario» le ore della fonte (programmazione
 * o rapportini) si tolgono e si aggiungono le loro ore del diario su questa commessa. Con
 * fonte Presenze il loro cartellino non crea ore sulle commesse, quindi non c'è niente da
 * togliere. Se il diario non si può leggere (blocchi === null) si resta come prima.
 */
export function vociConDiario(vociFonte = [], fonte, { blocchi = null, dipendenti = [], adesso = Date.now(), nomi = {} } = {}) {
  if (!Array.isArray(blocchi)) return vociFonte;
  const uid = uidConDiario(dipendenti);
  if (!uid.size) return vociFonte;
  const base = fonte === "presenze" ? vociFonte : senzaPersoneDiario(vociFonte, uid);
  return [...base, ...oreDaDiario(blocchi.filter(b => uid.has(b.uid)), adesso, nomi)];
}

/**
 * Grafico della Cabina di Regia (W409): ore della commessa settimana per settimana, ultime N
 * settimane fino a oggi (lunedì–domenica). elenco = riepilogoOre(...).elenco.
 * → [{ lunedi: "AAAA-MM-GG", etichetta: "13/10", ore, persone }]
 */
export function orePerSettimana(elenco = [], { oggi = new Date(), settimane = 10 } = {}) {
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const base = new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate());
  base.setDate(base.getDate() - ((base.getDay() + 6) % 7)); // lunedì di questa settimana
  const out = [];
  for (let i = settimane - 1; i >= 0; i--) {
    const l = new Date(base); l.setDate(base.getDate() - i * 7);
    const d = new Date(l); d.setDate(l.getDate() + 6);
    out.push({ lunedi: iso(l), domenica: iso(d), etichetta: `${String(l.getDate()).padStart(2, "0")}/${String(l.getMonth() + 1).padStart(2, "0")}`, ore: 0, nomi: new Set() });
  }
  elenco.forEach(g => {
    const s = out.find(x => g.data >= x.lunedi && g.data <= x.domenica);
    if (s) { s.ore += g.ore || 0; s.nomi.add(g.nome); }
  });
  return out.map(({ nomi, ...s }) => ({ ...s, ore: Math.round(s.ore * 100) / 100, persone: nomi.size }));
}

/**
 * W452 — costo della manodopera di un insieme di voci (es. quelle di un periodo), con le regole
 * del Controllo Economico: ore × costo orario del dipendente, collegato SOLO tramite l'account
 * (nexoraUid, W434), mai per nome. Le voci delle Presenze non si contano qui: il loro costo è
 * già nel movimento del cartellino. → { totale, persone: [{ nome, ore, costoOrario, totale }], senzaCosto }
 */
export function costoManodoperaVoci(voci = [], dipendenti = []) {
  const per = new Map();
  voci.filter(v => v && v.rif !== "Presenze" && v.ore > 0).forEach(v => {
    // come il Controllo Economico: prima l'account della cella della programmazione, poi quello della voce
    const dip = (v.uidCella && dipendenti.find(d => d.nexoraUid === v.uidCella)) || dipendenti.find(d => d.nexoraUid && d.nexoraUid === (v.uid || v.chiave)) || null;
    const uid = dip?.nexoraUid;
    const k = dip ? uid : `?${v.chiave || v.nome}`;
    const x = per.get(k) || { nome: dip ? `${dip.nome || ""} ${dip.cognome || ""}`.trim() || v.nome : v.nome, ore: 0, costoOrario: numIt(dip?.costoOrario) || 0, totale: 0 };
    x.ore += v.ore; per.set(k, x);
  });
  const persone = [...per.values()].map(x => ({ ...x, ore: Math.round(x.ore * 100) / 100, totale: Math.round(x.ore * x.costoOrario * 100) / 100 }));
  return {
    totale: Math.round(persone.reduce((t, p) => t + p.totale, 0) * 100) / 100,
    persone: persone.filter(p => p.totale > 0).sort((a, b) => b.totale - a.totale),
    senzaCosto: persone.filter(p => !(p.costoOrario > 0)).map(p => p.nome),
  };
}

/**
 * W453 — manodopera e noleggi calcolati ADESSO, con le stesse regole del Controllo Economico,
 * nella forma di budget.costiCalcolati (così totaliCommessa li usa allo stesso modo).
 * voci = ore della fonte dell'azienda già unite al diario (vociConDiario); noleggi = costo dei
 * noleggi mezzi d'opera (utils/noleggiUtils.js, costoNoleggio).
 */
export function costiCalcolatiDaVoci({ voci = [], dipendenti = [], fonteOre = "programmazione", noleggi = 0 } = {}) {
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const diario = costoManodoperaVoci(voci.filter(v => v.rif === "Le mie ore"), dipendenti).totale;
  const manodopera = fonteOre === "presenze" ? 0 : costoManodoperaVoci(voci.filter(v => v.rif !== "Le mie ore"), dipendenti).totale;
  return { manodopera: r2(manodopera), diario: r2(diario), noleggi: r2(noleggi), fonteOre };
}

/**
 * Se chi genera non può leggere i costi orari (Dipendenti) o il diario «Le mie ore», il calcolo
 * dal vivo darebbe zero per quelle voci: in quel caso valgono quelle salvate dal Controllo
 * Economico (che lo apre chi le può leggere). Mai un costo più basso solo per mancanza di permessi.
 */
export function unisciCostiCalcolati(ora, ore, salvati) {
  if (!ora) return null;
  if (!ore.dipendentiLeggibili && (ore.voci || []).some(v => v.rif !== "Presenze" && v.ore > 0)) return salvati || null;
  if (!ore.diarioLeggibile && salvati) return { ...ora, diario: Number(salvati.diario) || 0 };
  return ora;
}
